// SPDX-License-Identifier: BUSL-1.1

/**
 * CostSummaryCard — displays estimated BYOH runner costs.
 *
 * Shows total estimated cost from /api/tenant/vms (fetched alongside VM list).
 * Uses the cost estimation from vm-state.ts (uptime × provider rate).
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface VmPoolResponse {
  vms: unknown[];
  totalCostUsd: number;
}

export function CostSummaryCard({ className }: { className?: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-vms'],
    queryFn: () => apiFetch<VmPoolResponse>('/tenant/vms'),
    staleTime: 30_000,
  });

  const totalUsd = data?.totalCostUsd ?? 0;
  const vmCount = data?.vms.length ?? 0;

  if (isLoading) {
    return (
      <Card className={`p-4 ${className ?? ''}`}>
        <Skeleton className="mb-2 h-4 w-20" />
        <Skeleton className="mb-1 h-7 w-16" />
        <Skeleton className="h-3 w-28" />
      </Card>
    );
  }

  return (
    <Card className={`p-4 ${className ?? ''}`}>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-neutral-500">
        Estimated Cost
      </p>
      <p className="mb-1 font-mono text-2xl font-bold text-neutral-100">
        ${totalUsd.toFixed(4)}
      </p>
      <p className="text-xs text-neutral-500">
        {vmCount} active VM{vmCount !== 1 ? 's' : ''} · based on Hetzner CX21 rate
      </p>
    </Card>
  );
}